import { useContext } from 'react';
import type { WCmsPageView, WCmsPageEntityView } from '@zola/svc-web-api-ts-client';

import type { GenericEntityType } from 'actions/types/website/EntityActionTypes';
import { EditWebsiteContext, MainStateType, EntitiesStateType } from './context';
import { ActionTypes, EntitiesPropertyNames } from './actionTypes';

type Actions = {
  setActivePage: (pageType: string | undefined) => void;
  receivePage: (page: WCmsPageView) => void;
  receivePages: (pages: WCmsPageView[]) => void;
  receivePageAndActivate: (page: WCmsPageView) => void;
  receiveEntities: (entities: EntitiesStateType) => void;
  receiveReorderEntities: (
    newOrderedIds: WCmsPageEntityView[],
    entitiesName: EntitiesPropertyNames
  ) => void;
  receiveEntity: (entity: GenericEntityType, entitiesName: EntitiesPropertyNames) => void;
  receiveDeletedEntity: (entity: GenericEntityType, entitiesName: EntitiesPropertyNames) => void;
};

export const useEditWebsiteActions = (): {
  state: MainStateType;
  actions: Actions;
} => {
  const { state, dispatch } = useContext(EditWebsiteContext);

  const receivePages = (pages: WCmsPageView[]) => {
    return dispatch({
      type: ActionTypes.RECEIVE_PAGES,
      payload: {
        pages,
      },
    });
  };

  const receivePage = (page: WCmsPageView) => {
    return dispatch({
      type: ActionTypes.RECEIVE_PAGE,
      payload: {
        page,
      },
    });
  };

  const setActivePage = (pageType: string | undefined) => {
    return dispatch({
      type: ActionTypes.SET_ACTIVE_PAGE,
      payload: {
        activePageType: pageType,
      },
    });
  };

  const receivePageAndActivate = (page: WCmsPageView) => {
    receivePage(page);
    setActivePage(page.type);
  };

  const receiveEntities = (entities: { [k: string]: GenericEntityType[] }) => {
    return dispatch({
      type: ActionTypes.RECEIVE_ENTITIES,
      payload: {
        entities,
      },
    });
  };

  const receiveReorderEntities = (
    newOrderedIds: WCmsPageEntityView[],
    entitiesName: EntitiesPropertyNames
  ) => {
    const { entities } = state;
    const entitiesToUpdate = entities[entitiesName];
    const idsOnlyArr = newOrderedIds.map(e => e.entity_id);

    const finalPayload = entitiesToUpdate
      ?.slice()
      .sort(({ id: idA }, { id: idB }) => idsOnlyArr.indexOf(idA) - idsOnlyArr.indexOf(idB));

    return dispatch({
      type: ActionTypes.RECEIVE_ENTITIES,
      payload: {
        entities: {
          [entitiesName]: finalPayload,
        },
      },
    });
  };

  const receiveEntity = (entity: GenericEntityType, entitiesName: EntitiesPropertyNames) => {
    const { entities } = state;
    const entitiesToUpdate = entities[entitiesName];
    const existingEntity = entitiesToUpdate.findIndex(e => e.id === entity.id);
    if (existingEntity !== -1) entitiesToUpdate[existingEntity] = entity;
    else entitiesToUpdate.push(entity);
    return dispatch({
      type: ActionTypes.RECEIVE_ENTITIES,
      payload: {
        entities: {
          [entitiesName]: entitiesToUpdate,
        },
      },
    });
  };

  const receiveDeletedEntity = (entity: GenericEntityType, entitiesName: EntitiesPropertyNames) => {
    const { entities } = state;
    const entitiesToUpdate = entities[entitiesName];
    return dispatch({
      type: ActionTypes.RECEIVE_ENTITIES,
      payload: {
        entities: {
          [entitiesName]: entitiesToUpdate.filter(e => e.id !== entity.id),
        },
      },
    });
  };

  return {
    state,
    actions: {
      receivePages,
      setActivePage,
      receivePage,
      receivePageAndActivate,
      receiveEntities,
      receiveReorderEntities,
      receiveEntity,
      receiveDeletedEntity,
    },
  };
};
